import { useState } from 'react';
import * as XLSX from 'xlsx';
import { adminHeaders } from '../../lib/adminAuth';
import './BookingImportModal.css';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// Airbnb 予約取込モーダル
//   Airbnb の予約CSVを選択 → ブラウザでパース → 行配列(JSON)を
//   /api/admin/reservations/import-airbnb に送信して一括登録する。
//   物件は選択しない: CSVのI列「リスティング」(SD/FT/GW = booking_prefix)で自動判別。
//   金額は取り込まない（予約データのみ）。日付は MM/DD/YYYY 形式。
// ------------------------------------------------------------

interface Props {
  onClose: () => void;
  onImported: () => void;
}

interface ImportRow {
  ota_reservation_code: string; // 確認コード (C列)
  booking_code: string;         // リスティング I列 (SD/FT/GW)
  guest_name: string;           // ゲスト (H列)
  checkin_date: string;         // 開始日 (E列) → YYYY-MM-DD
  checkout_date: string;        // 終了日 (F列) → YYYY-MM-DD
  guest_count: number;          // 泊数ではなく人数が無いため、Airbnbは「ゲスト数」列が無い→ 0 のまま送る
  status: string;               // 種別 (B列)
}

interface ImportResult {
  ok: boolean;
  imported: number; skipped: number; errored: number; total: number;
  results: Array<{ row: number; code: string; prefix?: string; status: string; reason?: string }>;
}

// Airbnbヘッダー（日本語/英語）→ キー。
const HEADER_ALIASES: Record<string, string[]> = {
  ota_reservation_code: ['確認コード', 'Confirmation code', 'Confirmation'],
  booking_code: ['リスティング', 'Listing', '宿'],
  guest_name: ['ゲスト', 'Guest'],
  checkin_date: ['開始日', 'Start date', 'Check-in'],
  checkout_date: ['終了日', 'End date', 'Check-out'],
  guest_count: ['人数', 'of guests', 'adults', 'Guests'],
  status: ['種別', 'Type'],
};

// MM/DD/YYYY（Airbnb米国式） / YYYY-MM-DD / Date → 'YYYY-MM-DD'
function toYmd(v: any): string {
  if (v == null || v === '') return '';
  if (v instanceof Date && !isNaN(v.getTime())) {
    const y = v.getFullYear(), m = String(v.getMonth() + 1).padStart(2, '0'), d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // MM/DD/YYYY
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/); // YYYY-MM-DD
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return s;
}

function pickHeader(headers: string[], aliases: string[]): number {
  const norm = (x: string) => x.replace(/\s+/g, '').toLowerCase();
  const wanted = aliases.map(norm);
  // 完全一致を優先、その後部分一致
  for (let i = 0; i < headers.length; i++) if (wanted.includes(norm(headers[i] || ''))) return i;
  for (let i = 0; i < headers.length; i++) {
    const h = norm(headers[i] || '');
    if (wanted.some((w) => h.includes(w) || w.includes(h))) return i;
  }
  return -1;
}

export default function AirbnbImportModal({ onClose, onImported }: Props) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseMsg, setParseMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const onFile = async (file: File) => {
    setParseMsg(null); setResult(null); setRows([]); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
      if (aoa.length < 2) { setParseMsg({ ok: false, text: tx('abi.8') }); return; }
      const headers = (aoa[0] || []).map((x) => String(x ?? '').trim());

      const idx: Record<string, number> = {};
      Object.keys(HEADER_ALIASES).forEach((k) => { idx[k] = pickHeader(headers, HEADER_ALIASES[k]); });
      // 位置フォールバック（Airbnbの標準列順: C=2 確認コード, E=4 開始, F=5 終了, H=7 ゲスト, I=8 リスティング, B=1 種別）
      if (idx.ota_reservation_code < 0) idx.ota_reservation_code = 2;
      if (idx.checkin_date < 0) idx.checkin_date = 4;
      if (idx.checkout_date < 0) idx.checkout_date = 5;
      if (idx.guest_name < 0) idx.guest_name = 7;
      if (idx.booking_code < 0) idx.booking_code = 8;
      if (idx.status < 0) idx.status = 1;

      const out: ImportRow[] = [];
      for (let i = 1; i < aoa.length; i++) {
        const r = aoa[i] || [];
        const get = (k: string) => (idx[k] >= 0 ? r[idx[k]] : '');
        const checkin = toYmd(get('checkin_date'));
        const checkout = toYmd(get('checkout_date'));
        const prefix = String(get('booking_code') ?? '').trim();
        if (!checkin && !checkout && !prefix) continue; // 空行スキップ
        out.push({
          ota_reservation_code: String(get('ota_reservation_code') ?? '').trim(),
          booking_code: prefix,
          guest_name: String(get('guest_name') ?? '').trim(),
          checkin_date: checkin,
          checkout_date: checkout,
          guest_count: Math.max(1, Math.floor(Number(get('guest_count')) || 1)),
          status: String(get('status') ?? '').trim(),
        });
      }
      if (!out.length) { setParseMsg({ ok: false, text: tx('abi.9') }); return; }
      setRows(out);
      const prefixes = Array.from(new Set(out.map((o) => o.booking_code).filter(Boolean)));
      setParseMsg({ ok: true, text: tx('abi.10', { p0: out.length, p1: prefixes.join(', ') || '—' }) });
    } catch (e: any) {
      setParseMsg({ ok: false, text: tx('abi.11', { p0: e?.message || e }) });
    }
  };

  const submit = async () => {
    if (!rows.length) { setParseMsg({ ok: false, text: tx('abi.12') }); return; }
    setBusy(true); setResult(null);
    try {
      const res = await fetch('/api/admin/reservations/import-airbnb', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ rows }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setParseMsg({ ok: false, text: d.error || tx('abi.13') }); return; }
      setResult(d as ImportResult);
      setParseMsg(null);
      onImported();
    } catch (e: any) {
      setParseMsg({ ok: false, text: e?.message || tx('abi.13') });
    } finally { setBusy(false); }
  };

  return (
    <div className="bimp-overlay" onClick={() => !busy && onClose()}>
      <div className="bimp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bimp-head">
          <h2>{tx('abi.14')}</h2>
          <button className="bimp-x" onClick={onClose} disabled={busy}>×</button>
        </div>

        <p className="bimp-sub">
          {tx('abi.15')}
          {tx('abi.16')}
          {tx('abi.17')}
        </p>

        <div className="bimp-field">
          <label>{tx('abi.18')}</label>
          <input
            type="file"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
          />
          {fileName && <small className="bimp-filename">{fileName}</small>}
        </div>

        {parseMsg && <div className={parseMsg.ok ? 'bimp-msg bimp-msg-ok' : 'bimp-msg bimp-msg-err'}>{parseMsg.text}</div>}

        {result && (
          <div className="bimp-result">
            <div className="bimp-result-summary">
              <span className="bimp-chip bimp-chip-ok">{tx('abi.19')} {result.imported}</span>
              <span className="bimp-chip bimp-chip-skip">{tx('abi.20')} {result.skipped}</span>
              <span className="bimp-chip bimp-chip-err">{tx('abi.21')} {result.errored}</span>
              <span className="bimp-chip">{tx('abi.22')} {result.total}</span>
            </div>
            {(result.skipped > 0 || result.errored > 0) && (
              <div className="bimp-result-detail">
                <table>
                  <thead><tr><th>{tx('abi.23')}</th><th>{tx('abi.0')}</th><th>{tx('abi.24')}</th><th>{tx('abi.25')}</th><th>{tx('abi.26')}</th></tr></thead>
                  <tbody>
                    {result.results.filter((r) => r.status !== 'imported').map((r, i) => (
                      <tr key={i}>
                        <td>{r.row}</td>
                        <td>{r.code || '—'}</td>
                        <td>{r.prefix || '—'}</td>
                        <td className={r.status === 'error' ? 'st-err' : 'st-skip'}>{r.status === 'error' ? tx('abi.21') : tx('abi.20')}</td>
                        <td>{r.reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="bimp-actions">
          {result ? (
            <button className="bimp-btn bimp-btn-primary" onClick={onClose}>{tx('abi.27')}</button>
          ) : (
            <>
              <button className="bimp-btn bimp-btn-ghost" onClick={onClose} disabled={busy}>{tx('abi.28')}</button>
              <button className="bimp-btn bimp-btn-primary" onClick={submit} disabled={busy || !rows.length}>
                {busy ? tx('abi.29') : tx('abi.30', { p0: rows.length ? tx('abi.count', { n: rows.length }) : '' })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
